import { createBrowserRouter, RouterProvider, redirect } from "react-router";
import { RouteErrorScreen } from "./components/RouteErrorScreen";
import App, {
  ChatAliasRoute,
  CaniuseCapabilityRoute,
  EvalsRoute,
  HostCompareRoute,
  HostsRoute,
  OAuthFlowRoute,
  PlaygroundRoute,
  PromptsRoute,
  QueueRoute,
  ResourcesRoute,
  ServersRedirectRoute,
  ServersRoute,
  SettingsRoute,
  SkillsRoute,
  TasksRoute,
  ToolsRoute,
  TracingRoute,
} from "./App";
import { getAppRouter, setAppRouter } from "./router-ref";
import { buildHostsPath, routePaths } from "./lib/app-navigation";
import { APP_ROUTES } from "./lib/app-routes";

export { getAppRouter };

type AppRouter = ReturnType<typeof createBrowserRouter>;

/** Query string of a loader's request, so legacy redirects keep `?template=`. */
function searchOf(request: Request | undefined): string {
  if (!request?.url) return "";
  try {
    return new URL(request.url).search;
  } catch {
    return "";
  }
}

/**
 * The element each route renders, keyed by the path declared in
 * `app-routes.ts`. The table owns WHICH routes exist and what surface each
 * one belongs to; this map owns WHAT they render, because elements can't
 * live in a module the coverage tests import.
 *
 * Loaders (redirects) live here for the same reason.
 */
/**
 * EVERY key here must also be a path in `APP_ROUTES`.
 *
 * `buildRouteChildren` iterates `APP_ROUTES`, not this map, so a route
 * registered ONLY here is never mounted — the URL falls through to the `"*"`
 * catch-all and renders something else entirely, with no error anywhere.
 * The reverse direction throws loudly; this one fails silently, which is why
 * `router.test.tsx` asserts it.
 */
const ROUTE_ELEMENTS: Record<
  string,
  { element?: React.ReactElement; loader?: (args: any) => unknown }
> = {
  "/": { loader: () => redirect(routePaths.playground) },
  home: { loader: () => redirect(routePaths.playground) },
  connectors: { element: <ServersRoute /> },
  // Legacy `/servers` URLs redirect to canonical `/connectors` (the tab was
  // renamed Servers → Connectors).
  servers: {
    loader: ({ request }: any) =>
      redirect(`${routePaths.connectors}${searchOf(request)}`),
  },
  // Clients and Connectors are one page now — bare `/clients` is a redirect,
  // `/clients/:hostId` still opens that client's config canvas.
  clients: {
    loader: ({ request }: any) =>
      redirect(`${routePaths.connectors}${searchOf(request)}`),
  },
  "clients/:hostId": { element: <HostsRoute /> },
  "host-compare": { element: <HostCompareRoute /> },
  // Chrome-less host-compare surface for vanity domains (caniuse.dev):
  // App renders this full-bleed (no sidebar/header) and skips the
  // first-run onboarding redirect. `bare` forces the no-sub-nav render
  // even for signed-in users.
  "embed/host-compare": { element: <HostCompareRoute bare /> },
  "capabilities/:capabilitySlug": { element: <CaniuseCapabilityRoute /> },
  // Legacy `/hosts` URLs redirect to canonical `/clients` (the tab was
  // renamed Hosts → Clients). Route through `buildHostsPath` so the
  // `:hostId` deep-link is re-encoded exactly like canonical links
  // (router params arrive decoded; ids with reserved chars would
  // otherwise split into extra path segments and fail to match). The query
  // string rides along so `/hosts?template=claude` deep links keep working.
  hosts: {
    loader: ({ request }: any) =>
      redirect(`${buildHostsPath()}${searchOf(request)}`),
  },
  "hosts/:hostId": {
    loader: ({ params, request }: any) =>
      redirect(`${buildHostsPath(params.hostId)}${searchOf(request)}`),
  },
  tools: { element: <ToolsRoute /> },
  resources: { element: <ResourcesRoute /> },
  prompts: { element: <PromptsRoute /> },
  tasks: { element: <TasksRoute /> },
  skills: { element: <SkillsRoute /> },
  "oauth-flow": { element: <OAuthFlowRoute /> },
  tracing: { element: <TracingRoute /> },
  chat: { element: <ChatAliasRoute /> },
  // Catch sub-paths like `/chat/thread-1` so old bookmarks land on
  // Playground instead of the router's `*` catch-all (which would
  // render ServersRoute while `pathnameToActiveTab` still resolves
  // "chat" → "playground" — sidebar/content mismatch).
  "chat/*": { element: <ChatAliasRoute /> },
  playground: { element: <PlaygroundRoute /> },
  settings: { element: <SettingsRoute /> },
  // Legacy: the WorkOS API-keys page is gone; the pattern is pinned by the
  // settings surface manifest, so it renders the one Settings page.
  "settings/api-keys": { element: <SettingsRoute /> },
  "client-config": { element: <ServersRedirectRoute /> },
  evals: { element: <EvalsRoute /> },
  "evals/create": { element: <EvalsRoute /> },
  "evals/suite/:suiteId": { element: <EvalsRoute /> },
  "evals/suite/:suiteId/runs/:runId": { element: <EvalsRoute /> },
  "evals/suite/:suiteId/test/:testId": { element: <EvalsRoute /> },
  "evals/suite/:suiteId/test/:testId/edit": { element: <EvalsRoute /> },
  "evals/suite/:suiteId/edit": { element: <EvalsRoute /> },
  queue: { element: <QueueRoute /> },
  callback: { element: <ServersRoute /> },
  "oauth/callback/*": { element: <ServersRoute /> },
  "*": { element: <ServersRoute /> },
};

/** Route table → react-router children, preserving declaration order. */
function buildRouteChildren() {
  // THE OTHER DIRECTION, and the one that used to fail silently. This function
  // iterates `APP_ROUTES`, so a path registered only in `ROUTE_ELEMENTS` is
  // never mounted: the URL falls through to the `"*"` catch-all and renders a
  // different screen, with nothing in the console and no failing test — a
  // component test mounts the component directly, so it never notices.
  //
  // Checked here rather than only in a test because this is where the asymmetry
  // lives, and because a route that cannot be reached should stop the app at
  // startup rather than reach a user as a wrong page.
  const tablePaths = new Set(APP_ROUTES.map((route) => route.path));
  for (const path of Object.keys(ROUTE_ELEMENTS)) {
    if (!tablePaths.has(path)) {
      throw new Error(
        `[router] element registered for "${path}", which is not in APP_ROUTES — it would never be mounted`
      );
    }
  }

  return APP_ROUTES.map((route) => {
    const rendered = ROUTE_ELEMENTS[route.path];
    if (!rendered) {
      // A route table entry with nothing to render is a first-party bug —
      // the coverage test catches it, but fail loudly if one slips through.
      throw new Error(
        `[router] no element registered for route "${route.path}"`
      );
    }
    const isIndex = route.path === "/";
    return {
      ...(isIndex ? { index: true as const } : { path: route.path }),
      ...rendered,
    };
  });
}

/**
 * Phase 1 router: a single catch-all route renders the existing App.
 * Subsequent phases will replace the catch-all with a real route tree
 * (chrome layout + tab outlets + nested evals/orgs).
 */
export function createAppRouter(): AppRouter {
  const existing = getAppRouter();
  if (existing) return existing;
  const router = createBrowserRouter([
    ...(import.meta.env.DEV
      ? [
          {
            path: "__e2e/oauth-debugger",
            lazy: async () => {
              const { OAuthDebuggerE2EHarness } = await import(
                "./components/e2e/OAuthDebuggerE2EHarness"
              );
              return { Component: OAuthDebuggerE2EHarness };
            },
          },
        ]
      : []),
    {
      element: <App />,
      // The data router catches route render errors itself and renders the
      // nearest errorElement — the throw never reaches a React boundary above
      // <RouterProvider>. Without this a crashing route just blanks the app.
      errorElement: <RouteErrorScreen />,
      children: buildRouteChildren(),
    },
  ]);
  setAppRouter(router);
  return router;
}

export function AppRouterProvider() {
  const router = createAppRouter();
  return <RouterProvider router={router} />;
}
